import type { Dayjs } from "dayjs";
import React, { useCallback } from "react";
import TimePicker from "./TimePicker";
import { HourMinuteOption, Period, PERIODS, toStandardHours } from "./utils";

export interface TimePickerFromMomentProps {
  id: string;
  date: Dayjs;
  onChange: (date: Dayjs) => void;
  className?: string;
}

/**
 * Time picker that reads and emits Dayjs objects (also works with moment.js
 * objects at runtime via duck-typing).
 *
 * Use this when your time state is already a Dayjs/moment instance (common
 * with schedule components). Seconds are always reset to 0 on change.
 */
export const TimePickerFromMoment = ({
  id,
  date,
  onChange,
  className,
}: TimePickerFromMomentProps): JSX.Element => {
  const hours = date.hour() % 12;
  const minutes = date.minute();
  const period = date.format("A") as Period;

  const handleHoursChange = useCallback(
    (selectedItem: HourMinuteOption) => {
      if (!selectedItem) return;
      const currentPeriod = date.format("A") as Period;
      const newHours = toStandardHours(selectedItem.value, currentPeriod);
      onChange(date.hour(newHours).second(0) as Dayjs);
    },
    [date, onChange]
  );

  const handleMinutesChange = useCallback(
    (selectedItem: HourMinuteOption) => {
      if (!selectedItem) return;
      onChange(date.minute(selectedItem.value).second(0) as Dayjs);
    },
    [date, onChange]
  );

  const handleTimePeriodChange = useCallback(
    (newPeriod: Period) => {
      const hrs = date.hour();

      if (newPeriod === PERIODS.AM && hrs > 12) {
        onChange(date.hour(hrs - 12).second(0) as Dayjs);
      } else if (newPeriod === PERIODS.PM && hrs <= 12) {
        onChange(date.hour(hrs + 12).second(0) as Dayjs);
      }
    },
    [date, onChange]
  );

  return (
    <TimePicker
      id={id}
      hours={hours}
      minutes={minutes}
      period={period}
      onHoursChange={handleHoursChange}
      onMinutesChange={handleMinutesChange}
      onTimePeriodChange={handleTimePeriodChange}
      className={className}
    />
  );
};

export default TimePickerFromMoment;
